import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { useEntitlements } from '@/hooks/useEntitlements';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const { accountStatus, isExpired, loading: trialLoading } = useTrialStatus();
  const { isPro, isInternal, canBypassPaywall, canBypassTrial, loading: entitlementsLoading } = useEntitlements();

  // Show loading while checking auth and billing
  if (authLoading || trialLoading || entitlementsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  // ACCESS CONTROL: Full access if plan = 'pro' OR is_internal = true
  // Internal users already have canBypassPaywall && canBypassTrial = true
  // Pro users (account_status = 'active') have isPro = true
  const hasFullAccess = isPro || isInternal || (canBypassPaywall && canBypassTrial);
  
  if (hasFullAccess) {
    return <>{children}</>;
  }

  // BILLING GATE: Block access to /app routes if user doesn't have full access
  const isAppRoute = location.pathname.startsWith('/app');
  
  if (isAppRoute) {
    // Block if account is explicitly expired/cancelled (not trial/active)
    if (accountStatus === 'expired' || accountStatus === 'cancelled') {
      return <Navigate to="/pricing" replace state={{ from: location, reason: 'subscription_expired' }} />;
    }
    
    // Block if trial expired and user doesn't have full access
    // Redirect to pricing page to choose a plan
    if (isExpired && !hasFullAccess) {
      return <Navigate to="/pricing" replace state={{ from: location, reason: 'trial_expired' }} />;
    }
  }

  return <>{children}</>;
}

